import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { printMarksheet, MarksheetData } from './parts/marksheetPrintService';
import { printCertificate, CertificateData } from './parts/certificatePrintService';
import BatchSelector from './parts/BatchSelector';

interface Batch {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name?: string;
  course_title?: string;
  is_active: boolean;
}

interface Student {
  student_id: string;
  name: string;
  student_custom_id?: string;
  fatherName?: string;
  mobileNumber?: string;
  profilePicture?: any;
}

interface DocumentApiData {
  student: {
    student_id: string;
    name: string;
    guardian_relation: string;
    guardian_name: string;
    photo_url?: string | null;
    student_custom_id?: string | null;
  };
  course: { course_id: string; name: string; title: string };
  batch: { batch_id: string; batch_name: string; batch_time: string; start_date: string | null; end_date: string | null };
  institute: { name: string; address: string };
  enrollment: { enrollment_no: string; date_of_admission: string | null };
  certificate: { certificate_no: string; issue_date: string | null; issue_date_iso?: string | null };
  subjects: Array<{
    subject_id: string;
    subject_name: string;
    max_theory: number;
    max_lab: number;
    obtained_theory: number;
    obtained_lab: number;
  }>;
  totals: { max_total: number; obtained_total: number; percentage: number; grade: string };
}

// Per-student generation status, keyed by student_id — one call per batch
// via GET /v1/documents/batch/{batch_id}/status populates this whole map,
// so the table can show "already generated" badges without a request per row.
interface DocStatusEntry {
  certificate_no: string;
  issue_date: string | null;
  marksheet_generated: boolean;
  marksheet_generated_at: string | null;
  certificate_generated: boolean;
  certificate_generated_at: string | null;
}
type BatchStatusMap = Record<string, DocStatusEntry>;

// Marks-completeness status for the whole batch, from the existing
// GET /v1/marks/batch/{batch_id}/status endpoint (MarkController.php).
// total_subjects is the number of subjects on the batch's course; status
// maps student_id -> how many of those subjects have a marks row recorded.
interface MarksStatus {
  total_subjects: number;
  status: Record<string, number>;
}

function toMarksheetData(doc: DocumentApiData): MarksheetData {
  return {
    studentName: doc.student.name,
    fatherName: doc.student.guardian_name,
    courseName: doc.course.title || doc.course.name,
    enrollmentNo: doc.enrollment.enrollment_no,
    certificateNo: doc.certificate.certificate_no,
    batchSession:
      doc.batch.start_date && doc.batch.end_date
        ? `${doc.batch.start_date} - ${doc.batch.end_date}`
        : doc.batch.batch_name,
    issueDate: doc.certificate.issue_date || new Date().toLocaleDateString('en-GB'),
    photoUrl: doc.student.photo_url || undefined,
    subjects: doc.subjects.map((s) => ({
      subjectName: s.subject_name,
      maxTheory: s.max_theory,
      maxLab: s.max_lab,
      obtainedTheory: s.obtained_theory,
      obtainedLab: s.obtained_lab,
    })),
  };
}

function toCertificateData(doc: DocumentApiData): CertificateData {
  return {
    studentName: doc.student.name,
    guardianRelation: doc.student.guardian_relation,
    guardianName: doc.student.guardian_name,
    courseName: doc.course.title || doc.course.name,
    batchStartLabel: doc.batch.start_date || doc.batch.batch_name,
    batchEndLabel: doc.batch.end_date || '',
    studyCentreName: doc.institute.name,
    studyCentreAddress: doc.institute.address,
    grade: doc.totals.grade,
    percentage: doc.totals.percentage,
    enrollmentNo: doc.enrollment.enrollment_no,
    certificateNo: doc.certificate.certificate_no,
    issueDate: doc.certificate.issue_date || new Date().toLocaleDateString('en-GB'),
  };
}

const DocumentGenerator: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-row generating state, keyed by `${studentId}-${kind}`, so only the
  // button that was actually clicked shows a spinner/disables — not the
  // whole table.
  const [generatingKeys, setGeneratingKeys] = useState<Set<string>>(new Set());

  // Generation status for the whole batch (marksheet/certificate generated
  // + when), keyed by student_id. Re-synced from the server (not hand-merged
  // locally) right after every print, so it can never drift out of sync.
  const [batchStatus, setBatchStatus] = useState<BatchStatusMap>({});

  // Marks-completeness status for the whole batch — drives the guard that
  // blocks generation until every subject has marks recorded.
  const [marksStatus, setMarksStatus] = useState<MarksStatus | null>(null);

  // Fetch batches on mount
  useEffect(() => {
    fetchBatches();
  }, []);

  // Fetch students + generation status + marks status when batch changes
  useEffect(() => {
    if (selectedBatchId) {
      fetchStudentsForBatch(selectedBatchId);
      fetchBatchStatus(selectedBatchId);
      fetchMarksStatus(selectedBatchId);
    } else {
      setStudents([]);
      setBatchStatus({});
      setMarksStatus(null);
    }
  }, [selectedBatchId]);

  const fetchBatches = async () => {
    try {
      setLoadingBatches(true);
      const response = await apiClient.get('/v1/batches');
      if (response.data.success) {
        const activeBatches = response.data.data.filter((b: Batch) => b.is_active);
        setBatches(activeBatches);
        if (activeBatches.length > 0 && !selectedBatchId) {
          setSelectedBatchId(activeBatches[0].batch_id);
        }
      } else {
        setError('Failed to load batches');
      }
    } catch (err: any) {
      setError('Error loading batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchStudentsForBatch = async (batchId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/v1/students/get-students-by-batch/${batchId}?status=active&limit=200`);
      if (response.data.success) {
        const list = response.data.data.map((s: any) => ({
          student_id: s.student_id,
          name: s.name,
          student_custom_id: s.student_custom_id,
          fatherName: s.fatherName,
          mobileNumber: s.mobileNumber,
          profilePicture: s.profilePicture,
        }));
        setStudents(list);
      } else {
        setError('Failed to load students');
      }
    } catch (err: any) {
      setError('Error loading students');
    } finally {
      setLoading(false);
    }
  };

  // Single source of truth for "is this already generated" — called on
  // batch load AND right after every print, so the UI never has to rely on
  // hand-merged local state that can drift out of sync with the server.
  const fetchBatchStatus = async (batchId: string) => {
    try {
      const response = await apiClient.get(`/v1/documents/batch/${batchId}/status`);
      if (response.data.success) {
        setBatchStatus(response.data.data || {});
      }
    } catch (err) {
      // Non-fatal: table still works, it just won't show "already
      // generated" badges until the next successful fetch.
    }
  };

  // Existing endpoint from MarkController.php — no new backend code needed.
  const fetchMarksStatus = async (batchId: string) => {
    try {
      const response = await apiClient.get(`/v1/marks/batch/${batchId}/status`);
      if (response.data.success) {
        setMarksStatus(response.data.data || null);
      }
    } catch (err) {
      // Non-fatal, but note: if this fails we fall back to blocking
      // generation entirely (see isMarksComplete) rather than silently
      // allowing incomplete marksheets through.
      setMarksStatus(null);
    }
  };

  // A student's marks are "complete" only when every subject on the course
  // has a recorded row AND the course actually has subjects. Missing/failed
  // marksStatus is treated as incomplete (fail closed), not as "allowed".
  const isMarksComplete = useCallback(
    (studentId: string): boolean => {
      if (!marksStatus || marksStatus.total_subjects <= 0) return false;
      const recorded = marksStatus.status[studentId] ?? 0;
      return recorded >= marksStatus.total_subjects;
    },
    [marksStatus]
  );

  const marksIncompleteMessage = (studentId: string): string => {
    if (!marksStatus) return 'Marks status could not be loaded.';
    if (marksStatus.total_subjects <= 0) return 'No subjects configured for this course.';
    const recorded = marksStatus.status[studentId] ?? 0;
    return `Marks incomplete: ${recorded} of ${marksStatus.total_subjects} subjects recorded.`;
  };

  const fetchDocumentData = useCallback(async (studentId: string, batchId: string): Promise<DocumentApiData> => {
    const res = await apiClient.get(`/v1/documents/student/${studentId}/batch/${batchId}`);
    if (!res.data.success) {
      throw new Error(res.data.error || 'Failed to load document data');
    }
    return res.data.data as DocumentApiData;
  }, []);

  // Tells the backend a document was actually printed. Rather than
  // hand-merging the PUT's response into local state — which is easy to
  // get subtly wrong and silently fall out of sync with the server — we
  // re-fetch the whole batch status afterwards. That's the exact same call
  // a page reload makes, so whatever makes the badge correct on reload now
  // makes it correct immediately too, because it's the same code path.
  const markGenerated = useCallback(async (studentId: string, batchId: string, type: 'marksheet' | 'certificate') => {
    try {
      await apiClient.put(`/v1/documents/student/${studentId}/batch/${batchId}/mark-generated`, { type });
    } finally {
      await fetchBatchStatus(batchId);
    }
  }, []);

  const withGeneratingKey = async (key: string, fn: () => Promise<void>) => {
    setGeneratingKeys((prev) => new Set(prev).add(key));
    try {
      await fn();
    } finally {
      setGeneratingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Issue date is fixed automatically by the backend the first time a
  // document is generated for a student — there is no modal or date input
  // here, and no role (including admin) can edit it from the UI.
  //
  // Both handlers re-check isMarksComplete() before doing any work, as a
  // second line of defense behind the disabled button — protects against
  // stale marksStatus in a long-lived tab, or the button being triggered
  // programmatically.
  const handlePrintMarksheet = (student: Student) => {
    if (!selectedBatchId) return;
    setError(null);
    if (!isMarksComplete(student.student_id)) {
      setError(`Cannot generate marksheet for ${student.name}: ${marksIncompleteMessage(student.student_id)}`);
      return;
    }
    void withGeneratingKey(`${student.student_id}-marksheet`, async () => {
      try {
        const doc = await fetchDocumentData(student.student_id, selectedBatchId);
        await printMarksheet(toMarksheetData(doc));
        await markGenerated(student.student_id, selectedBatchId, 'marksheet');
      } catch (err: any) {
        setError(err.message || `Failed to generate marksheet for ${student.name}`);
      }
    });
  };

  const handlePrintCertificate = (student: Student) => {
    if (!selectedBatchId) return;
    setError(null);
    if (!isMarksComplete(student.student_id)) {
      setError(`Cannot generate certificate for ${student.name}: ${marksIncompleteMessage(student.student_id)}`);
      return;
    }
    void withGeneratingKey(`${student.student_id}-certificate`, async () => {
      try {
        const doc = await fetchDocumentData(student.student_id, selectedBatchId);
        await printCertificate(toCertificateData(doc));
        await markGenerated(student.student_id, selectedBatchId, 'certificate');
      } catch (err: any) {
        setError(err.message || `Failed to generate certificate for ${student.name}`);
      }
    });
  };

  const renderStudentName = (rowData: Student) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Avatar
          image={rowData.profilePicture?.cdn_url}
          icon={!rowData.profilePicture ? 'pi pi-user' : undefined}
          size="normal"
          shape="circle"
          style={{ width: '36px', height: '36px', backgroundColor: '#E8EEF7', color: '#0e225d' }}
        />
        <div>
          <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#0e225d' }}>{rowData.name}</div>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{rowData.student_custom_id || ''}</div>
        </div>
      </div>
    );
  };

  const renderDocButton = (opts: {
    label: string;
    reprintLabel: string;
    isBusy: boolean;
    isGenerated: boolean;
    generatedAt: string | null;
    isBlocked: boolean;
    blockedReason: string;
    color: string;
    icon: string;
    onClick: () => void;
  }) => {
    const { label, reprintLabel, isBusy, isGenerated, generatedAt, isBlocked, blockedReason, color, icon, onClick } = opts;
    const isDisabled = isBusy || isBlocked;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <button
          onClick={onClick}
          disabled={isDisabled}
          title={
            isBlocked
              ? blockedReason
              : isGenerated
              ? `Generated on ${generatedAt || 'an earlier date'}. Click to reprint (issue date won't change).`
              : `Generate & print ${label.toLowerCase()}`
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            background: isBlocked ? '#f9fafb' : isGenerated ? '#f0fdf4' : 'white',
            border: `1.5px solid ${isBlocked ? '#d1d5db' : isGenerated ? '#16a34a' : color}`,
            borderRadius: '8px',
            color: isBlocked ? '#9ca3af' : isGenerated ? '#16a34a' : color,
            fontSize: '0.72rem',
            fontWeight: '700',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isBusy ? 0.6 : isBlocked ? 0.75 : 1,
            transition: 'all 0.2s ease',
          }}
        >
          {isBusy ? (
            <i className="pi pi-spin pi-spinner"></i>
          ) : isBlocked ? (
            <i className="pi pi-lock"></i>
          ) : isGenerated ? (
            <i className="pi pi-check-circle"></i>
          ) : (
            <i className={icon}></i>
          )}
          {isGenerated && !isBlocked ? reprintLabel : label}
        </button>
        {isBlocked ? (
          <span style={{ fontSize: '0.62rem', color: '#b45309', fontWeight: '600', textAlign: 'center', maxWidth: '140px' }}>
            {blockedReason}
          </span>
        ) : (
          isGenerated &&
          generatedAt && (
            <span style={{ fontSize: '0.62rem', color: '#9ca3af', fontWeight: '600' }}>
              Printed {generatedAt}
            </span>
          )
        )}
      </div>
    );
  };

  const renderActions = (rowData: Student) => {
    const marksheetKey = `${rowData.student_id}-marksheet`;
    const certificateKey = `${rowData.student_id}-certificate`;
    const isMarksheetBusy = generatingKeys.has(marksheetKey);
    const isCertificateBusy = generatingKeys.has(certificateKey);

    const status = batchStatus[rowData.student_id];
    const isMarksheetGenerated = !!status?.marksheet_generated;
    const isCertificateGenerated = !!status?.certificate_generated;

    // Marks completeness gates both document types — a marksheet lists
    // per-subject scores and a certificate carries the overall grade, so
    // neither should be generated with subjects still unrecorded.
    const marksComplete = isMarksComplete(rowData.student_id);
    const blockedReason = marksIncompleteMessage(rowData.student_id);

    return (
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {renderDocButton({
          label: 'Marksheet',
          reprintLabel: 'Reprint Marksheet',
          isBusy: isMarksheetBusy,
          isGenerated: isMarksheetGenerated,
          generatedAt: status?.marksheet_generated_at ?? null,
          isBlocked: !marksComplete,
          blockedReason,
          color: '#0e225d',
          icon: 'pi pi-print',
          onClick: () => handlePrintMarksheet(rowData),
        })}
        {renderDocButton({
          label: 'Certificate',
          reprintLabel: 'Reprint Certificate',
          isBusy: isCertificateBusy,
          isGenerated: isCertificateGenerated,
          generatedAt: status?.certificate_generated_at ?? null,
          isBlocked: !marksComplete,
          blockedReason,
          color: '#b02a2a',
          icon: 'pi pi-verified',
          onClick: () => handlePrintCertificate(rowData),
        })}
      </div>
    );
  };

  const colHeaderStyle: React.CSSProperties = {
    background: '#fafafa',
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E5E7EB',
    padding: '12px 16px',
    letterSpacing: '0.6px',
  };
  const colBodyStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderBottom: '1px solid #f3f4f6',
  };

  const selectedBatch = batches.find((b) => b.batch_id === selectedBatchId);

  if (loadingBatches) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" />
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Batch Selection */}
      <BatchSelector batches={batches} selectedBatchId={selectedBatchId} onSelect={setSelectedBatchId} />

      {selectedBatchId && (
        <>
          {/* Error */}
          {error && <Message severity="error" text={error} style={{ width: '100%' }} />}

          {/* Marks-incomplete banner for the batch as a whole */}
          {marksStatus && marksStatus.total_subjects > 0 && (
            (() => {
              const incompleteCount = students.filter((s) => !isMarksComplete(s.student_id)).length;
              return incompleteCount > 0 ? (
                <Message
                  severity="warn"
                  text={`${incompleteCount} of ${students.length} students have incomplete marks. Their Marksheet/Certificate buttons are disabled until all ${marksStatus.total_subjects} subjects are recorded.`}
                  style={{ width: '100%' }}
                />
              ) : null;
            })()
          )}

          {/* Students Table */}
          <div style={{ background: 'white', borderRadius: '14px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
                <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0e225d' }}>
                  Document Generator
                  {selectedBatch && (
                    <span style={{ fontSize: '0.7rem', fontWeight: '400', color: '#5f636b', marginLeft: '8px' }}>
                      {selectedBatch.batch_name}
                    </span>
                  )}
                </h2>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
              </div>
            ) : (
              <DataTable
                value={students}
                dataKey="student_id"
                emptyMessage="No students enrolled in this batch."
                style={{ fontSize: '0.85rem' }}
                tableStyle={{ tableLayout: 'fixed', width: '100%' }}
              >
                <Column
                  field="name"
                  header="Student"
                  body={renderStudentName}
                  headerStyle={{ ...colHeaderStyle, width: '24%' }}
                  bodyStyle={{ ...colBodyStyle, width: '24%' }}
                />
                <Column
                  field="fatherName"
                  header="Father's Name"
                  body={(rowData: Student) => rowData.fatherName || <span style={{ color: '#c1c7cf' }}>—</span>}
                  headerStyle={{ ...colHeaderStyle, width: '16%' }}
                  bodyStyle={{ ...colBodyStyle, width: '16%', color: '#374151', fontSize: '0.85rem' }}
                />
                <Column
                  field="mobileNumber"
                  header="Mobile"
                  body={(rowData: Student) => rowData.mobileNumber || <span style={{ color: '#c1c7cf' }}>—</span>}
                  headerStyle={{ ...colHeaderStyle, width: '12%' }}
                  bodyStyle={{ ...colBodyStyle, width: '12%', color: '#374151', fontSize: '0.85rem' }}
                />
                <Column
                  header="Marks"
                  alignHeader="center"
                  body={(rowData: Student) => {
                    if (!marksStatus || marksStatus.total_subjects <= 0) {
                      return <span style={{ color: '#c1c7cf', fontSize: '0.78rem' }}>—</span>;
                    }
                    const recorded = marksStatus.status[rowData.student_id] ?? 0;
                    const complete = recorded >= marksStatus.total_subjects;
                    return (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: '999px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          background: complete ? '#f0fdf4' : '#fffbeb',
                          color: complete ? '#16a34a' : '#b45309',
                          border: `1px solid ${complete ? '#bbf7d0' : '#fde68a'}`,
                        }}
                      >
                        {recorded}/{marksStatus.total_subjects}
                      </span>
                    );
                  }}
                  headerStyle={{ ...colHeaderStyle, width: '10%' }}
                  bodyStyle={{ ...colBodyStyle, width: '10%', textAlign: 'center' }}
                />
                <Column
                  header="Generate"
                  alignHeader="center"
                  body={renderActions}
                  headerStyle={{ ...colHeaderStyle, width: '38%' }}
                  bodyStyle={{ ...colBodyStyle, width: '38%', textAlign: 'center', paddingTop: '12px', paddingBottom: '12px' }}
                />
              </DataTable>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentGenerator;